import * as React from "react";
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Paper,
  Tabs,
  Tab,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

function SectionTitle({ overline, title, subtitle }) {
  return (
    <Box textAlign="center" mb={6}>
      {overline && (
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
          {overline}
        </Typography>
      )}
      <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="h6" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function useRitesData() {
  // 昇光編（写真指示のみ）
  const SHOKO = [
    { no: 1, title: "点火の儀", photo: "ロウソクを灯し、その光を撮る" },
    { no: 2, title: "水鏡の儀", photo: "水面やコップの水に空・自分を映して撮る" },
    { no: 3, title: "風送りの儀", photo: "風に揺れる布や木々を撮る" },
    { no: 4, title: "奉仕の儀（社会貢献）", photo: "拾ったゴミ（袋や成果）を撮る", social: true },
    { no: 5, title: "芽吹きの儀", photo: "草木や花の新芽を撮る" },
    { no: 6, title: "音霊の儀", photo: "声を出す前後の自分の笑顔を撮る" },
    { no: 7, title: "光浴びの儀", photo: "太陽の光を浴び両手を広げる姿を撮る" },
    { no: 8, title: "花結びの儀（社会貢献）", photo: "花を一輪贈った／飾った写真を撮る", social: true },
    { no: 9, title: "道開きの儀", photo: "はじめて歩く道や場所を撮る" },
    { no: 10, title: "星仰ぎの儀", photo: "夜空や星（難しければ月・夜空）を撮る" },
    { no: 11, title: "大地踏みの儀", photo: "裸足で土や芝に立ち、足元を撮る" },
    { no: 12, title: "お裾分けの儀（社会貢献）", photo: "食べ物や品物を分けた場面を撮る", social: true },
    { no: 13, title: "太陽礼拝の儀", photo: "日の出／日中の太陽に祈る姿を撮る" },
    { no: 14, title: "水音の儀", photo: "川・滝・海など水辺の写真を撮る" },
    { no: 15, title: "炎舞の儀", photo: "キャンドルや焚き火の炎を撮る" },
    { no: 16, title: "贈与の儀（社会貢献）", photo: "小さな寄付のレシートや贈り物を撮る", social: true },
    { no: 17, title: "羽ばたきの儀", photo: "鳥や羽根モチーフ、空に伸びる手を撮る" },
    { no: 18, title: "雨受けの儀", photo: "雨の滴を掌で受けて撮る" },
    { no: 19, title: "共鳴の儀", photo: "仲間の投稿を真似して同じポーズを撮る" },
    { no: 20, title: "助け合いの儀（社会貢献）", photo: "誰かを手助けした痕跡を撮る", social: true },
    { no: 21, title: "夏至光の儀", photo: "太陽を背に光を受ける姿を撮る" },
    { no: 22, title: "鏡返しの儀", photo: "鏡に映る自分の笑顔を撮る" },
    { no: 23, title: "祝祭の儀", photo: "自分へのご褒美（食事や花）を撮る" },
    { no: 24, title: "感謝奉納の儀（社会貢献）", photo: "祈りの場で手を合わせた写真を撮る", social: true },
    { no: 25, title: "静謐の儀", photo: "夜明けの空や静かな景色を撮る" },
    { no: 26, title: "光送りの儀", photo: "誰かに送ったメッセージや贈り物を撮る" }
  ];

  // 帰光編（写真＋問い）
  const KIKO = [
    { no: 1, title: "夏至光受けの儀", photo: "掌に太陽の光を受ける", q: "今、あなたが世界から受け取っている光は？" },
    { no: 2, title: "影映しの儀", photo: "自分の影を撮る", q: "あなたの影（弱さや恐れ）はどんな形？" },
    { no: 3, title: "月光の儀", photo: "夜空や月を撮る", q: "人に照らされて気づく自分は？" },
    { no: 4, title: "奉仕の儀（社会貢献）", photo: "不要品をリサイクルする", q: "あなたが人に渡せるものは物以外に何？", social: true },
    { no: 5, title: "読経の儀", photo: "経文や祈りの言葉を撮る", q: "あなたの心を落ち着かせる言葉は？" },
    { no: 6, title: "収穫感謝の儀", photo: "旬の果物や食事を撮る", q: "今年あなたが収穫できたものは？" },
    { no: 7, title: "追憶の儀", photo: "アルバムや思い出の品を撮る", q: "感謝したい過去の出来事は？" },
    { no: 8, title: "感謝贈与の儀（社会貢献）", photo: "手紙やカードを渡す", q: "その人に伝えたい感謝のひとことは？", social: true },
    { no: 9, title: "浄火の儀", photo: "不要な紙を燃やす", q: "あなたが手放したいものは？" },
    { no: 10, title: "水浄めの儀", photo: "手を水で清める", q: "新しく清めたい部分はどこですか？" },
    { no: 11, title: "沈黙の儀", photo: "静かな空間や瞑想の姿を撮る", q: "沈黙の中で心に浮かぶ思いは？" },
    { no: 12, title: "お裾分けの儀（社会貢献）", photo: "分けた食材や贈り物を撮る", q: "人と分かち合いたい豊かさは？", social: true },
    { no: 13, title: "月待ちの儀", photo: "満月や新月の夜空を撮る", q: "次の満ち欠けまでに育てたい心は？" },
    { no: 14, title: "祈念の儀", photo: "祈る姿を撮る", q: "今、最も祈りたいことは？" },
    { no: 15, title: "音静めの儀", photo: "鈴や鐘を撮る", q: "その音を聞いたとき心はどんな状態？" },
    { no: 16, title: "掃奉仕の儀（社会貢献）", photo: "掃除した成果を撮る", q: "きれいに整えたい人間関係は？", social: true },
    { no: 17, title: "鏡の儀", photo: "鏡に映る自分を撮る", q: "今の自分を一言で表すなら？" },
    { no: 18, title: "書記の儀", photo: "祈りや願いを書いた紙を撮る", q: "未来に残したい言葉は？" },
    { no: 19, title: "灯火返しの儀", photo: "ロウソクを吹き消す", q: "今年終わらせたいものは？" },
    { no: 20, title: "助け合いの儀（社会貢献）", photo: "誰かを助ける姿を撮る", q: "もっと人の役に立てる場面はどこ？", social: true },
    { no: 21, title: "雪受けの儀", photo: "雪や氷を掌にのせる（なければ白い布）", q: "あなたの心に凍っているものは？" },
    { no: 22, title: "心結びの儀", photo: "大切な物を撮る", q: "それを通して誰とつながりたいですか？" },
    { no: 23, title: "灯籠の儀", photo: "小さな灯りを撮る", q: "心に灯し続けたい光は？" },
    { no: 24, title: "奉納の儀（社会貢献）", photo: "奉納した品や祈りの場を撮る", q: "あなたが社会に奉納したいものは？", social: true },
    { no: 25, title: "冬至祈光の儀", photo: "一年を振り返り、灯した光を撮る", q: "最も光を感じた瞬間はいつ？" },
    { no: 26, title: "光還りの儀", photo: "一年で最も大切だった物を撮る", q: "これから心に宿したい光は？" }
  ];

  const SEASONALS = [
    { key: "春分", title: "芽吹きの大儀式", desc: "花の写真を投稿し、花畑モザイクとして公開。", icon: <AutoAwesomeIcon /> },
    { key: "夏至", title: "光の頂点の大儀式", desc: "太陽に両手を広げた写真を集め、光輪サークルを生成。", icon: <WbSunnyIcon /> },
    { key: "秋分", title: "収穫と感謝の大儀式", desc: "実りの写真を束ね、黄金の稲穂アートに。", icon: <FavoriteBorderIcon /> },
    { key: "冬至", title: "光還りの大儀式", desc: "1万人の写真＋コメントで光の星図を完成。", icon: <NightsStayIcon /> }
  ];

  return { SHOKO, KIKO, SEASONALS };
}

function Hero() {
  return (
    <Box component="section" id="hero" sx={{ position: "relative", color: "#fff", py: { xs: 12, md: 18 }, background: "radial-gradient(1200px 600px at 20% -10%, rgba(255,255,255,0.25), transparent 60%), linear-gradient(160deg,#0b1020,#0e1240)", overflow: "hidden" }}>
      <Container maxWidth="lg">
        <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.8 }}>
          2025年 冬至 開幕
        </Typography>
        <Typography variant="h2" sx={{ fontWeight: 900, lineHeight: 1.1, mt: 1 }}>
          Rites of Light
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9, mt: 2 }}>
          光を放ち、光を還す一年 ― 52の小さな儀式と、4つの大儀式。
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4}>
          <Button href="#signup" size="large" variant="contained" color="primary">
            先行案内に登録する
          </Button>
          <Button href="#structure" size="large" variant="outlined" color="inherit" startIcon={<CalendarMonthIcon />}>
            プログラムを見る
          </Button>
        </Stack>
      </Container>
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <Box sx={{ position: "absolute", top: 40, right: 80, opacity: 0.6, filter: "blur(1px)" }}>
          <AutoAwesomeIcon fontSize="large" />
        </Box>
        <Box sx={{ position: "absolute", bottom: 60, left: 60, opacity: 0.4 }}>
          <StarBorderIcon fontSize="large" />
        </Box>
      </Box>
    </Box>
  );
}

function StructureSection() {
  return (
    <Box component="section" id="structure" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="lg">
        <SectionTitle overline="構成" title="一年の巡礼" subtitle="52の小さな儀式と、4つの大儀式" />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <WbSunnyIcon color="warning" />
                  <Typography variant="h5" fontWeight={800}>昇光編（冬至→夏至）</Typography>
                </Stack>
                <Typography color="text.secondary" mb={2}>
                  外向きエネルギーを高める26週。毎週の儀式は「写真でシェアできる行動」だけ。4週に1回、社会貢献のワークを配置。
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="灯火" />
                  <Chip label="水鏡" />
                  <Chip label="花結び" />
                  <Chip label="太陽礼拝" />
                  <Chip label="大地踏み" />
                  <Chip icon={<VolunteerActivismIcon />} label="社会貢献" color="success" variant="outlined" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <NightsStayIcon color="info" />
                  <Typography variant="h5" fontWeight={800}>帰光編（夏至→冬至）</Typography>
                </Stack>
                <Typography color="text.secondary" mb={2}>
                  内向きエネルギーを整える26週。毎週「写真＋問い」で心に光を宿す。4週に1回、社会貢献のワークを配置。
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="月光" />
                  <Chip label="影映し" />
                  <Chip label="浄火" />
                  <Chip label="沈黙" />
                  <Chip label="書記" />
                  <Chip icon={<VolunteerActivismIcon />} label="社会貢献" color="success" variant="outlined" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function Seasonals() {
  const { SEASONALS } = useRitesData();
  return (
    <Box component="section" id="seasonals" sx={{ py: { xs: 10, md: 14 }, background: (t) => t.palette.mode === 'light' ? "#fafafa" : "#0c0f1a" }}>
      <Container maxWidth="lg">
        <SectionTitle overline="大儀式" title="季節の巡り" subtitle="春分・夏至・秋分・冬至の非同期イベント" />
        <Grid container spacing={3}>
          {SEASONALS.map((s) => (
            <Grid item xs={12} md={3} key={s.key}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    {s.icon}
                    <Typography variant="h6" fontWeight={800}>{s.key}</Typography>
                  </Stack>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>{s.title}</Typography>
                  <Typography color="text.secondary">{s.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function WeeksTabs() {
  const { SHOKO, KIKO } = useRitesData();
  const [tab, setTab] = React.useState(0);

  return (
    <Box component="section" id="weeks" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="lg">
        <SectionTitle overline="週次の儀式" title="52の光のステップ" subtitle="昇光編：写真のみ／帰光編：写真＋問い" />

        <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: (t) => `1px solid ${t.palette.divider}` }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab icon={<WbSunnyIcon />} iconPosition="start" label="昇光編（冬至→夏至）" />
              <Tab icon={<NightsStayIcon />} iconPosition="start" label="帰光編（夏至→冬至）" />
            </Tabs>
          </Box>
          <Divider />
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {tab === 0 && (
              <Grid container spacing={2}>
                {SHOKO.map((w) => (
                  <Grid item xs={12} md={6} key={w.no}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip size="small" label={`Week ${w.no}`} />
                          {w.social && <Chip size="small" color="success" icon={<VolunteerActivismIcon />} label="社会貢献" />}
                        </Stack>
                        <Typography variant="h6" fontWeight={800} mt={1}>{w.title}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                          <CameraAltIcon fontSize="small" />
                          <Typography color="text.secondary">{w.photo}</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            {tab === 1 && (
              <Grid container spacing={2}>
                {KIKO.map((w) => (
                  <Grid item xs={12} key={w.no}>
                    <Accordion disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                          <Chip size="small" label={`Week ${w.no}`} />
                          {w.social && <Chip size="small" color="success" icon={<VolunteerActivismIcon />} label="社会貢献" />}
                          <Typography variant="h6" fontWeight={800}>{w.title}</Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CameraAltIcon fontSize="small" />
                              <Typography color="text.secondary">{w.photo}</Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">問い</Typography>
                            <Typography>{w.q}</Typography>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

function TimelineSection() {
  return (
    <Box component="section" id="timeline" sx={{ py: { xs: 10, md: 14 }, background: (t) => t.palette.mode === 'light' ? "#fff7ee" : "#141013" }}>
      <Container maxWidth="lg">
        <SectionTitle overline="スケジュール" title="2025→2026の流れ" subtitle="先行案内から冬至ローンチまで" />
        <Grid container spacing={3}>
          {[{ when: "2025 秋", title: "先行案内スタート", desc: "世界観テキスト公開／早期登録受付" }, { when: "2025 12月 初旬", title: "正式申込開始", desc: "参加ガイド・FAQ公開" }, { when: "2025 12/21（冬至）", title: "開幕・点火の儀", desc: "全員一斉スタート" }, { when: "2026 春分/夏至/秋分", title: "季節の大儀式（非同期）", desc: "花畑／光輪／稲穂アート公開" }, { when: "2026 12/20（冬至）", title: "光の星図 完成", desc: "1万人の写真＋コメントで銀河を描く" }].map((i, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{i.when}</Typography>
                  <Typography variant="h6" fontWeight={800} mt={0.5}>{i.title}</Typography>
                  <Typography color="text.secondary" mt={0.5}>{i.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function SignupSection() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: API接続
    console.log({ name, email });
    setSubmitted(true);
  };

  return (
    <Box component="section" id="signup" sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="sm">
        <SectionTitle overline="先行案内" title="冬至の点火に、参加しますか？" subtitle="メールアドレスを登録すると最新情報をお届けします。" />
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
          {submitted ? (
            <Stack spacing={2} alignItems="center">
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" fontWeight={800}>登録ありがとうございます！</Typography>
              <Typography color="text.secondary">冬至の開幕に向け、続報をお待ちください。</Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField label="お名前（任意）" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                <TextField label="メールアドレス" required type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button type="submit" variant="contained" size="large">先行案内に登録</Button>
                  <Button href="#weeks" variant="text" size="large">内容をもっと見る</Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

function Footer() {
  return (
    <Box component="footer" sx={{ py: 6, background: (t) => t.palette.mode === 'light' ? "#0b1020" : "#05070e", color: "#fff" }}>
      <Container maxWidth="lg">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h6" fontWeight={800}>Rites of Light</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © {new Date().getFullYear()} Rites of Light / マスターマインド
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={2} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
              <MuiLink href="#structure" color="inherit" underline="hover">構成</MuiLink>
              <MuiLink href="#seasonals" color="inherit" underline="hover">大儀式</MuiLink>
              <MuiLink href="#weeks" color="inherit" underline="hover">52週</MuiLink>
              <MuiLink href="#signup" color="inherit" underline="hover">先行案内</MuiLink>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function PricingSection() {
  const tiers = [
    {
      name: "Standard",
      price: "¥60,000 / 年",
      highlight: false,
      features: [
        "52の小さな儀式に参加",
        "季節の大儀式（非同期）",
        "光のアルバム（個人）",
        "修了証（デジタル）",
        "星図への掲載"
      ]
    },
    {
      name: "Plus",
      price: "¥90,000 / 年",
      highlight: true,
      features: [
        "Standardの全て",
        "季節ごとのライブ解説録画",
        "優先サポート",
        "限定ワーク（年4回）"
      ]
    },
    {
      name: "Patron",
      price: "¥150,000 / 年",
      highlight: false,
      features: [
        "Plusの全て",
        "主宰からの年2回メッセージ",
        "星図での特別バッジ",
        "限定コミュニティサークル"
      ]
    }
  ];

  return (
    <Box component="section" id="pricing" sx={{ py: { xs: 10, md: 14 }, background: (t) => t.palette.mode === 'light' ? "#0a0f1a" : "#0a0f1a" }}>
      <Container maxWidth="lg">
        <SectionTitle overline="料金" title="参加プラン" subtitle="必要十分なStandard／充実のPlus／応援のPatron" />
        <Grid container spacing={3}>
          {tiers.map((t) => (
            <Grid item xs={12} md={4} key={t.name}>
              <Card sx={{ height: "100%", border: t.highlight ? '2px solid #ffd166' : undefined }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={800}>{t.name}</Typography>
                    {t.highlight && <Chip color="primary" label="おすすめ" />}
                  </Stack>
                  <Typography variant="h4" fontWeight={900} mt={1}>{t.price}</Typography>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    {t.features.map((f, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemText primary={f} />
                      </ListItem>
                    ))}
                  </List>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1}>
                    <Button href="#signup" fullWidth variant={t.highlight ? 'contained' : 'outlined'}>このプランで先行案内</Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

function FAQSection({ onOpenModal }) {
  const faqs = [
    { q: "写真は必須ですか？", a: "各週の儀式は“写真でシェアできる行動”が基本です。難しい週はテキスト投稿でもOKです。" },
    { q: "途中参加できますか？", a: "いつでも参加可能です。参加した週からカウントされ、星図には参加回数に応じて掲載されます。" },
    { q: "非同期の大儀式って？", a: "指定の週に投稿された写真が自動でアートに集約され、最終日に完成版を公開します。" },
    { q: "個人情報は守られますか？", a: "公開名はニックネーム可。写真のメタデータは公開前に除去されます。" },
    { q: "支払い方法は？", a: "クレジットカード/銀行振込を予定。法人請求書発行も相談可。" },
    { q: "解約・返金は？", a: "開幕前のキャンセルは全額返金。開始後は残期間に応じた按分返金規定を案内予定です。" }
  ];
  return (
    <Box component="section" id="faq" sx={{ py: { xs: 10, md: 14 }, background: (t) => t.palette.mode === 'light' ? "#0c0f1a" : "#0c0f1a" }}>
      <Container maxWidth="md">
        <SectionTitle overline="FAQ" title="よくある質問" subtitle="詳細はモーダルでもご確認いただけます" />
        <Stack spacing={1}>
          {faqs.map((f, idx) => (
            <Accordion key={idx} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Typography variant="subtitle1" fontWeight={700}>{f.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
        <Stack alignItems="center" mt={4}>
          <Button onClick={onOpenModal} variant="outlined">よくある質問をまとめて見る</Button>
        </Stack>
      </Container>
    </Box>
  );
}

function FAQModal({ open, onClose }) {
  const items = [
    { q: "参加対象は？", a: "自己探求やスピリチュアルに関心のある方ならどなたでも。50代以降の女性が多いですが、年齢性別は問いません。" },
    { q: "毎週の所要時間は？", a: "10〜30分程度。写真を撮り、短い投稿を行うだけです。" },
    { q: "社会貢献の具体例は？", a: "寄付、リサイクル、掃除奉仕、手紙贈与、ちょっとした助け合い等、身近でできる行いです。" },
    { q: "星図の公開範囲は？", a: "Web上に常設し、ニックネームと写真・短文コメントを表示します。検索で自分の星を光らせる機能を予定。" },
    { q: "途中休んだ週は？", a: "後追い投稿OK。無理なく続けられるよう非同期設計です。" },
    { q: "企業・団体での参加は？", a: "まとめてのお申込み/請求書対応が可能です。事務局までご相談ください。" }
  ];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>よくある質問</DialogTitle>
      <DialogContent dividers>
        <List>
          {items.map((it, idx) => (
            <ListItem key={idx} alignItems="flex-start">
              <ListItemText primary={it.q} secondary={it.a} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
        <Button href="#signup" variant="contained">先行案内に登録</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RitesOfLightLP() {
  const theme = React.useMemo(
    () =>
      responsiveFontSizes(
        createTheme({
          palette: {
            mode: "dark",
            primary: { main: "#ffd166" },
            secondary: { main: "#6c63ff" },
            background: {
              default: "#080b18",
              paper: "#101426",
            },
          },
          shape: { borderRadius: 16 },
          typography: {
            fontFamily: "'Noto Sans JP', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Apple Color Emoji', 'Segoe UI Emoji'",
            h2: { fontWeight: 900 },
            h3: { fontWeight: 900 },
            h5: { fontWeight: 700 },
          },
          components: {
            MuiButton: {
              styleOverrides: { root: { textTransform: "none", borderRadius: 9999, paddingLeft: 20, paddingRight: 20 } },
            },
            MuiCard: { styleOverrides: { root: { borderRadius: 20 } } },
          },
        })
      ),
    []
  );

  const [faqOpen, setFaqOpen] = React.useState(false);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="sticky" color="transparent" elevation={0} sx={{ backdropFilter: "blur(8px)", borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Toolbar>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>Rites of Light</Typography>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={2}>
            <Button href="#structure" color="inherit">構成</Button>
            <Button href="#seasonals" color="inherit">大儀式</Button>
            <Button href="#weeks" color="inherit">52週</Button>
            <Button href="#pricing" color="inherit">料金</Button>
            <Button onClick={() => setFaqOpen(true)} color="inherit">FAQ</Button>
            <Button href="#signup" variant="contained" color="primary">先行案内</Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <main>
        <Hero />
        <StructureSection />
        <Seasonals />
        <WeeksTabs />
        <TimelineSection />
        <PricingSection />
        <FAQSection onOpenModal={() => setFaqOpen(true)} />
        <SignupSection />
      </main>

      <Footer />

      <FAQModal open={faqOpen} onClose={() => setFaqOpen(false)} />
    </ThemeProvider>
  );
}
